import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { unwrapList, apiErrorMessage } from '@/lib/api-helpers';
import { Button } from '@ouropix/ui';
import { Card, CardContent } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { Textarea } from '@ouropix/ui';
import { Switch } from '@ouropix/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ouropix/ui';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ouropix/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import { Plus, Edit, Trash2, GripVertical, Star, Layers, ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { GameModality } from '@/components/games/types';

const PAGE_SIZE = 20;

const ANIMATION_TYPES = [
  { value: 'default', label: 'Padrao' },
  { value: 'slot', label: 'Slot (Roleta de simbolos)' },
  { value: 'spin', label: 'Slot (CacaPix)' },
  { value: 'scratch', label: 'Raspadinha' },
  { value: 'wheel', label: 'Roleta' },
  { value: 'box', label: 'Caixa Misteriosa' },
  { value: 'open', label: 'Bau (Abertura)' },

];

export const Route = createFileRoute('/_admin/jogos/modalidades')({
  component: ModalidadesPage,
});

interface SortableRowProps {
  modality: GameModality & { preview_image_url?: string | null };
  onEdit: () => void;
  onDelete: () => void;
  onTogglePopular: () => void;
}

function SortableRow({ modality, onEdit, onDelete, onTogglePopular }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: modality.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  return (
    <TableRow ref={setNodeRef} style={style} className={cn(isDragging && 'opacity-50 bg-muted')}>
      <TableCell className="hidden sm:table-cell">
        <Button variant="ghost" size="icon" aria-label="Reordenar" {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing h-8 w-8">
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </Button>
      </TableCell>
      <TableCell>
        {modality.preview_image_url ? (
          <img src={modality.preview_image_url} alt={modality.name} className="h-10 w-10 rounded-lg object-cover" loading="lazy" />
        ) : (
          <span className="text-2xl">{modality.icon || '🎮'}</span>
        )}
      </TableCell>
      <TableCell>
        <div>
          <p className="font-medium">{modality.name}</p>
          {modality.description && (
            <p className="text-xs text-muted-foreground line-clamp-1">{modality.description}</p>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell">
        <code className="bg-muted px-2 py-1 rounded text-sm">{modality.code}</code>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <Badge variant="outline">
          {ANIMATION_TYPES.find(a => a.value === modality.animation_type)?.label || modality.animation_type}
        </Badge>
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="icon"
          onClick={onTogglePopular}
          className={cn(
            'h-8 w-8 rounded-full transition-colors',
            modality.is_popular ? 'bg-yellow-500/20 text-yellow-500' : 'text-muted-foreground hover:text-yellow-500'
          )}
          title={modality.is_popular ? 'Remover do Popular' : 'Marcar como Popular'}
          aria-label="Popular"
        >
          <Star className={cn('h-4 w-4', modality.is_popular && 'fill-current')} />
        </Button>
      </TableCell>
      <TableCell>
        <Badge variant={modality.active ? 'default' : 'secondary'}>
          {modality.active ? 'Ativo' : 'Inativo'}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Editar">
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onDelete} className="text-destructive hover:text-destructive" aria-label="Excluir">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

const DEFAULT_FORM = {
  name: '',
  code: '',
  description: '',
  icon: '',
  animation_type: 'default',
  active: true,
  is_popular: false,
  preview_image_url: '',
};

function ModalidadesPage() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedModality, setSelectedModality] = useState<GameModality | null>(null);
  const [modalityToDelete, setModalityToDelete] = useState<GameModality | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const { data: modalities, isLoading } = useQuery<GameModality[]>({
    queryKey: ['admin-game-modalities'],
    queryFn: async () => {
      const r = await api.get('/admin/modalities');
      const raw: any[] = unwrapList(r.data, 'modalities');
      return raw.map((m: any) => ({
        id: m.id,
        name: m.name,
        code: m.code ?? '',
        description: m.description ?? null,
        icon: m.icon ?? null,
        animation_type: m.animationType ?? m.animation_type ?? 'default',
        active: m.active ?? true,
        order_index: m.orderIndex ?? m.order_index ?? 0,
        is_popular: m.isPopular ?? m.is_popular ?? false,
        created_at: m.created_at ?? '',
        preview_image_url: m.previewImageUrl ?? m.preview_image_url ?? null,
      })) as GameModality[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (selectedModality) {
        await api.put(`/admin/modalities/${selectedModality.id}`, data);
      } else {
        const maxOrder = modalities?.length ?? 0;
        await api.post('/admin/modalities', { ...data, order_index: maxOrder });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-game-modalities'] });
      toast.success(selectedModality ? 'Modalidade atualizada!' : 'Modalidade criada!');
      handleCloseForm();
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Erro ao salvar'));
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: { id: string; order_index: number }[]) => {
      await api.patch('/admin/modalities/reorder', { items });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-game-modalities'] });
      toast.success('Ordem atualizada!');
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Erro ao reordenar'));
    },
  });

  const togglePopularMutation = useMutation({
    mutationFn: async ({ id, is_popular }: { id: string; is_popular: boolean }) => {
      await api.patch(`/admin/modalities/${id}`, { is_popular });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-game-modalities'] });
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Erro ao atualizar'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/modalities/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-game-modalities'] });
      toast.success('Modalidade excluida!');
      setModalityToDelete(null);
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'Erro ao excluir modalidade'));
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && modalities) {
      const oldIndex = modalities.findIndex(m => m.id === active.id);
      const newIndex = modalities.findIndex(m => m.id === over.id);
      const newOrder = arrayMove(modalities, oldIndex, newIndex);
      const updates = newOrder.map((item, index) => ({ id: item.id, order_index: index }));
      reorderMutation.mutate(updates);
    }
  };

  const handleOpenForm = (modality?: GameModality) => {
    if (modality) {
      setSelectedModality(modality);
      setFormData({
        name: modality.name,
        code: modality.code,
        description: modality.description || '',
        icon: modality.icon || '',
        animation_type: modality.animation_type,
        active: modality.active,
        is_popular: modality.is_popular,
        preview_image_url: (modality as any).previewImageUrl || (modality as any).preview_image_url || '',
      });
    } else {
      setSelectedModality(null);
      setFormData(DEFAULT_FORM);
    }
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedModality(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.code) { toast.error('Nome e código são obrigatórios'); return; }
    saveMutation.mutate(formData);
  };

  const allModalities = modalities ?? [];
  const totalPages = Math.ceil(allModalities.length / PAGE_SIZE);
  const paginatedModalities = allModalities.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Layers}
        title="Modalidades de Jogos"
        description="Arraste para reordenar - Clique na estrela para marcar como Popular"
        actions={
          <Button onClick={() => handleOpenForm()}>
            <Plus className="mr-2 h-4 w-4" />
            Nova Modalidade
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 hidden sm:table-cell"></TableHead>
                  <TableHead>Ícone / Preview</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead className="hidden sm:table-cell">Código</TableHead>
                  <TableHead className="hidden md:table-cell">Animação</TableHead>
                  <TableHead className="w-16">Popular</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">Carregando...</TableCell>
                  </TableRow>
                ) : allModalities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Nenhuma modalidade cadastrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={paginatedModalities.map(m => m.id)} strategy={verticalListSortingStrategy}>
                      {paginatedModalities.map(modality => (
                        <SortableRow
                          key={modality.id}
                          modality={modality as any}
                          onEdit={() => handleOpenForm(modality)}
                          onDelete={() => setModalityToDelete(modality)}
                          onTogglePopular={() => togglePopularMutation.mutate({ id: modality.id, is_popular: !modality.is_popular })}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-xs text-muted-foreground">
                Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, allModalities.length)} de {allModalities.length}
              </p>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} aria-label="Página anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs px-2 text-muted-foreground">{currentPage} / {totalPages}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} aria-label="Próxima página">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedModality ? 'Editar Modalidade' : 'Nova Modalidade'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mod-name">Nome *</Label>
                <Input id="mod-name" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Slot Machine" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mod-code">Código *</Label>
                <Input id="mod-code" value={formData.code} onChange={e => setFormData(p => ({ ...p, code: String(e.target.value || '').toLowerCase() }))} placeholder="Ex: slot" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mod-icon">Ícone (Emoji)</Label>
                <Input id="mod-icon" value={formData.icon} onChange={e => setFormData(p => ({ ...p, icon: e.target.value }))} placeholder="Ex: 🎰" />
              </div>
              <div className="space-y-2">
                <Label>Tipo de Animação</Label>
                <Select value={formData.animation_type} onValueChange={v => setFormData(p => ({ ...p, animation_type: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {ANIMATION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Preview Image URL — TODO: substituir por ImageCropModal quando disponível */}
            <div className="space-y-2">
              <Label htmlFor="mod-preview" className="flex items-center gap-1">
                <ImageIcon className="h-4 w-4" />
                URL de Preview
              </Label>
              <Input
                id="mod-preview"
                value={formData.preview_image_url}
                onChange={e => setFormData(p => ({ ...p, preview_image_url: e.target.value }))}
                placeholder="https://..."
              />
              {formData.preview_image_url && (
                <img src={formData.preview_image_url} alt="Preview" className="h-20 w-20 rounded-lg object-cover border" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="mod-desc">Descrição</Label>
              <Textarea id="mod-desc" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Descrição da modalidade..." rows={3} />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="mod-popular">Exibir em "Populares"</Label>
              <Switch id="mod-popular" checked={formData.is_popular} onCheckedChange={v => setFormData(p => ({ ...p, is_popular: v }))} />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="mod-active">Ativo</Label>
              <Switch id="mod-active" checked={formData.active} onCheckedChange={v => setFormData(p => ({ ...p, active: v }))} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>Cancelar</Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        isOpen={!!modalityToDelete}
        onClose={() => setModalityToDelete(null)}
        onConfirm={() => { if (modalityToDelete) deleteMutation.mutate(modalityToDelete.id); }}
        title="Confirmar Exclusão"
        description={`Tem certeza que deseja excluir a modalidade "${modalityToDelete?.name}"?`}
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
      />
    </div>
  );
}
